import {
  addStoryMs,
  civilParts,
  compareStory,
  formatStoryClock,
  isoFromStoryMs,
  timeZoneAbbrev,
  storyDeltaMs,
  storyIsoOnDay,
  storyMs,
} from './storyTime'
import type { CameraBeat, DisplayUnits } from './types'

/** Elapsed from scenario start: `m:ss`, `h:mm:ss`, or whole seconds as `m:ss`. */
export function parseElapsed(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const hms = trimmed.match(/^(\d+):([0-5]?\d):([0-5]?\d)$/)
  if (hms) {
    return (Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3])) * 1000
  }
  const ms = trimmed.match(/^(\d+):([0-5]?\d)$/)
  if (ms) return (Number(ms[1]) * 60 + Number(ms[2])) * 1000
  return null
}

export function formatElapsed(ms: number): string {
  const clamped = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(clamped / 3600)
  const m = Math.floor((clamped % 3600) / 60)
  const s = clamped % 60
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Preview / Play button runtime label: `mm:ss` or `h:mm:ss` (minutes always zero-padded). */
export function formatPlaybackRuntime(ms: number): string {
  const clamped = Math.max(0, Math.round(ms / 1000))
  const h = Math.floor(clamped / 3600)
  const m = Math.floor((clamped % 3600) / 60)
  const s = clamped % 60
  const mm = m.toString().padStart(2, '0')
  const ss = s.toString().padStart(2, '0')
  if (h > 0) return `${h}:${mm}:${ss}`
  // Pad minutes so 03:00 and 28:00 share width and don't shove the strip slider.
  return `${mm}:${ss}`
}

/**
 * Preview / Play starts at 1× of the current content span (left end of the slider).
 * Right end stays the 30s floor via minPlaybackRuntimeMs.
 */
export function defaultPlaybackRuntimeMs(fullMs: number): number {
  const full = Math.max(0, fullMs)
  if (full <= 0) return 60_000
  return full
}

/** Minimum real-time duration the runtime slider allows (30s, or full length when the board is shorter). */
export function minPlaybackRuntimeMs(fullMs: number): number {
  const full = Math.max(0, fullMs)
  const floor = 30_000
  if (full <= 0) return floor
  return Math.min(full, floor)
}

/**
 * Preview speed bounds: left = 1× (script wall length), right = fastest allowed by the 30s floor.
 * No slower-than-realtime; overview/testing keeps the old fast end.
 */
export const PLAYBACK_RATE_MIN = 1

/** Friendly rate ticks between 1× and the board’s max (30s floor). */
const PLAYBACK_RATE_TICKS = [
  1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80, 100, 120, 150, 200,
] as const

export function playbackRateBounds(fullMs: number): { minRate: number; maxRate: number } {
  const full = Math.max(1, fullMs)
  const floor = minPlaybackRuntimeMs(full)
  const maxRate = Math.max(PLAYBACK_RATE_MIN, full / Math.max(1, floor))
  return { minRate: PLAYBACK_RATE_MIN, maxRate }
}

/** Discrete rates for the strip slider — 1× … max, only round-ish steps. */
export function playbackRateTicks(maxRate: number): number[] {
  const cap = Math.max(PLAYBACK_RATE_MIN, maxRate)
  const ticks: number[] = [PLAYBACK_RATE_MIN]
  for (const r of PLAYBACK_RATE_TICKS) {
    if (r <= PLAYBACK_RATE_MIN + 1e-6) continue
    if (r >= cap - 1e-6) break
    ticks.push(r)
  }
  // Always land the right end on the true 30s-floor rate (avoids 00:31 from a nearby 4× tick).
  if (cap > PLAYBACK_RATE_MIN + 1e-6) {
    const last = ticks[ticks.length - 1]!
    if (last > cap + 1e-9) ticks[ticks.length - 1] = cap
    else if (Math.abs(last - cap) > 1e-9) ticks.push(cap)
  }
  return ticks
}

export function snapPlaybackRate(rate: number, maxRate: number): number {
  const ticks = playbackRateTicks(maxRate)
  let best = ticks[0]!
  let bestDist = Math.abs(rate - best)
  for (const t of ticks) {
    const d = Math.abs(rate - t)
    if (d < bestDist) {
      best = t
      bestDist = d
    }
  }
  return best
}

/** Curated rates for the icon speed menu (1× … board max / 30s floor). */
const SPEED_MENU_RATES = [1, 1.5, 2, 3, 4, 5, 8, 10, 15, 20, 30, 60, 120, 200] as const

export function playbackSpeedMenuRates(maxRate: number): number[] {
  const cap = Math.max(PLAYBACK_RATE_MIN, maxRate)
  const out: number[] = []
  for (const r of SPEED_MENU_RATES) {
    if (r <= cap + 1e-9) out.push(r)
  }
  if (cap > PLAYBACK_RATE_MIN + 1e-6) {
    const last = out[out.length - 1]!
    if (Math.abs(last - cap) > 1e-6) out.push(cap)
  }
  return out
}

/** @deprecated Prefer discrete playbackRateTicks indices. */
export function playbackRateFromSliderT(t: number, maxRate: number): number {
  const ticks = playbackRateTicks(maxRate)
  if (ticks.length <= 1) return PLAYBACK_RATE_MIN
  const u = Math.min(1, Math.max(0, t))
  const idx = Math.round(u * (ticks.length - 1))
  return ticks[idx]!
}

/** @deprecated Prefer discrete playbackRateTicks indices. */
export function playbackSliderTFromRate(rate: number, maxRate: number): number {
  const ticks = playbackRateTicks(maxRate)
  if (ticks.length <= 1) return 0
  const snapped = snapPlaybackRate(rate, maxRate)
  const idx = Math.max(0, ticks.indexOf(snapped))
  return idx / (ticks.length - 1)
}

export function formatPlaybackRate(rate: number): string {
  const rounded = Math.round(rate * 100) / 100
  if (Math.abs(rounded - 1) < 0.02) return '1×'
  if (Math.abs(rounded - 1.5) < 0.05) return '1.5×'
  if (Math.abs(rounded - 2.5) < 0.05) return '2.5×'
  // Whole rates and the 30s-floor cap (e.g. 33.1×) use the nearest whole rate.
  // A ~multiple-of-5 label is not a menu choice, so the chip and the list disagreed.
  if (rounded > 20 || Number.isInteger(rounded) || Math.abs(rounded - Math.round(rounded)) < 0.05) {
    return `${Math.round(rounded)}×`
  }
  const one = Math.round(rounded * 10) / 10
  return `${one}×`
}

/**
 * Absolute wire instants (create / Go Live / schedule ends that are true UTC).
 * Bare ISO datetimes (no Z/offset) follow the server UTC contract — not browser-local parse.
 */
export function parseAbsoluteInstant(iso: string): Date {
  const s = iso.trim()
  if (!s) return new Date(NaN)
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return new Date(`${s}Z`)
  return new Date(s)
}

/** Card / rail wall clock in the viewer's local timezone. */
export function formatWall(
  iso: string,
  opts?: {
    /** Include :ss — use for chat where many lines share the same minute. */
    seconds?: boolean
  },
): string {
  const d = parseAbsoluteInstant(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(opts?.seconds ? { second: '2-digit' as const } : {}),
  })
}

/**
 * Human duration for session cards / docs — whole minutes only
 * (`3 hrs 59 min`, or `1 day 22 hrs 40 min` when ≥ 24h).
 * Pass `keepSeconds` only when a live ticker truly needs second resolution.
 */
export function formatDuration(ms: number, opts?: { keepSeconds?: boolean }): string {
  const keepSeconds = Boolean(opts?.keepSeconds)
  if (!keepSeconds) {
    const totalMin = Math.max(0, Math.round(ms / 60_000))
    if (totalMin === 0) return '0 min'
    const days = Math.floor(totalMin / (24 * 60))
    const hrs = Math.floor((totalMin % (24 * 60)) / 60)
    const mins = totalMin % 60
    const parts: string[] = []
    if (days) parts.push(days === 1 ? '1 day' : `${days} days`)
    if (hrs) parts.push(hrs === 1 ? '1 hr' : `${hrs} hrs`)
    if (mins || (!days && !hrs)) parts.push(`${mins} min`)
    return parts.join(' ')
  }
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  if (totalSec === 0) return '0 sec'
  const days = Math.floor(totalSec / 86_400)
  const hrs = Math.floor((totalSec % 86_400) / 3600)
  const mins = Math.floor((totalSec % 3600) / 60)
  const secs = totalSec % 60
  const parts: string[] = []
  if (days) parts.push(days === 1 ? '1 day' : `${days} days`)
  if (hrs) parts.push(hrs === 1 ? '1 hr' : `${hrs} hrs`)
  if (mins || days || hrs) parts.push(`${mins} min`)
  parts.push(`${secs} sec`)
  return parts.join(' ')
}

export function windCardinal(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return dirs[Math.round((((deg % 360) + 360) % 360) / 45) % 8] ?? 'N'
}

/** Minutes from midnight; accepts `H:MM`, `H:MM:SS`, optional am/pm. */
export function parseHm(hm: string): number | null {
  const t = hm.trim()
  const m = t.match(/^(\d{1,2}):([0-5]?\d)(?::([0-5]?\d))?\s*(am|pm)?$/i)
  if (!m) return null
  let h = Number(m[1])
  const min = Number(m[2])
  const ap = m[4]?.toLowerCase()
  if (ap) {
    if (h < 1 || h > 12) return null
    if (ap === 'pm' && h < 12) h += 12
    if (ap === 'am' && h === 12) h = 0
  } else if (h > 23) return null
  return h * 60 + min
}

/** Seconds from midnight; accepts `H:MM`, `H:MM:SS`, optional am/pm. */
export function parseHmToSeconds(hm: string): number | null {
  const t = hm.trim()
  const m = t.match(/^(\d{1,2}):([0-5]?\d)(?::([0-5]?\d))?\s*(am|pm)?$/i)
  if (!m) return null
  let h = Number(m[1])
  const min = Number(m[2])
  const sec = m[3] != null ? Number(m[3]) : 0
  const ap = m[4]?.toLowerCase()
  if (ap) {
    if (h < 1 || h > 12) return null
    if (ap === 'pm' && h < 12) h += 12
    if (ap === 'am' && h === 12) h = 0
  } else if (h > 23) return null
  return h * 3600 + min * 60 + sec
}

export function addSecondsToHm(hm: string, deltaSec: number): string | null {
  const s = parseHmToSeconds(hm)
  if (s == null) return null
  const day = (((s + deltaSec) % 86400) + 86400) % 86400
  const h = Math.floor(day / 3600)
  const min = Math.floor((day % 3600) / 60)
  const sec = day % 60
  if (sec === 0) return `${h}:${min.toString().padStart(2, '0')}`
  return `${h}:${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

/** Normalize offset entry to 24h `H:MM:SS` (no am/pm). Bare `H:MM` becomes `:00`. */
export function normalizeClockOffsetHm(text: string): string | null {
  const sec = parseHmToSeconds(text)
  if (sec == null) return null
  const day = ((sec % 86400) + 86400) % 86400
  const h = Math.floor(day / 3600)
  const min = Math.floor((day % 3600) / 60)
  const s = day % 60
  return `${h}:${min.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

/** Display stamps (12h with AM/PM): `hm` = h:mm a; `hms` = h:mm:ss a. */
function formatHm12Stamp(totalSec: number, mode: 'hm' | 'hms'): string {
  const day = ((Math.floor(totalSec) % 86400) + 86400) % 86400
  const h = Math.floor(day / 3600)
  const min = Math.floor((day % 3600) / 60)
  const sec = day % 60
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  const base = `${h12}:${min.toString().padStart(2, '0')}`
  if (mode === 'hm') return `${base} ${suffix}`
  return `${base}:${sec.toString().padStart(2, '0')} ${suffix}`
}

/**
 * Parse typed wall-clock input to seconds-from-midnight.
 * Requires AM/PM when the hour is 1–12 (bare `10:50` is ambiguous) — unless
 * `preferWindow` is set, in which case bare times pick the meridian that falls
 * inside [startSec, endSec] (scenario script boards).
 */
export function parseWallClockInputToSeconds(
  hm: string,
  preferWindow?: { startSec: number; endSec: number | null },
): number | null {
  const t = hm.trim()
  const m = t.match(/^(\d{1,2}):([0-5]?\d)(?::([0-5]?\d))?\s*(am|pm)?$/i)
  if (!m) return null
  const h = Number(m[1])
  const ap = m[4]
  if (!ap && h >= 1 && h <= 12) {
    if (!preferWindow) return null
    const am = parseHmToSeconds(`${t} am`)
    const pm = parseHmToSeconds(`${t} pm`)
    const { startSec, endSec } = preferWindow
    const inWin = (s: number | null) =>
      s != null && s >= startSec - 0.5 && (endSec == null || s <= endSec + 0.5)
    if (inWin(am) && !inWin(pm)) return am
    if (inWin(pm) && !inWin(am)) return pm
    if (inWin(am) && inWin(pm)) {
      // Both in window (rare overnight span) — prefer the one closer to start.
      return Math.abs((am as number) - startSec) <= Math.abs((pm as number) - startSec) ? am : pm
    }
    // Outside window: pick the first at/after start, else AM.
    const after = [am, pm]
      .filter((s): s is number => s != null && s >= startSec - 0.5)
      .sort((a, b) => a - b)
    return after[0] ?? am ?? pm
  }
  return parseHmToSeconds(t)
}

function looksLikeClockTime(text: string): boolean {
  return /^\d{1,2}:[0-5]?\d(?::[0-5]?\d)?(\s*(am|pm))?$/i.test(text.trim())
}

/** Normalize typed wall clock to stored 24h `H:MM:SS`; rejects ambiguous 1–12 without AM/PM. */
export function normalizeWallClockInput(text: string): string | null {
  const sec = parseWallClockInputToSeconds(text)
  if (sec == null) return null
  const day = ((sec % 86400) + 86400) % 86400
  const h = Math.floor(day / 3600)
  const min = Math.floor((day % 3600) / 60)
  const s = day % 60
  return `${h}:${min.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

/** Format stored 24h offset for editing UI (12h with AM/PM). */
export function formatWallClockDisplay(hm: string, mode: 'hm' | 'hms' = 'hms'): string {
  const sec = parseHmToSeconds(hm)
  if (sec == null) return hm.trim()
  return formatHm12Stamp(sec, mode)
}

/** Authoring clock: story instants on the scenario's calendar day, in its timezone. */
export type ScenarioClockConfig = {
  startsAt: string
  timezone: string
  /** Display span used when a bare clock time needs a meridian window. */
  durationMs: number
  timeFormat?: 'hm' | 'hms'
  showTimeZone?: boolean
}

function storyClockWithSeconds(clock: ScenarioClockConfig): boolean {
  return (clock.timeFormat ?? 'hms') === 'hms'
}

function wallSecondsToIso(wallSec: number, clock: ScenarioClockConfig): string {
  const day = ((Math.floor(wallSec) % 86400) + 86400) % 86400
  const h = Math.floor(day / 3600)
  const min = Math.floor((day % 3600) / 60)
  const s = day % 60
  return storyIsoOnDay(clock.startsAt, h, min, s, clock.timezone)
}

/** Value for `<input type="time">` (HH:MM:SS) from a story instant, or empty. */
export function authorTimeToTimeInputValue(
  iso: string | null | undefined,
  clock: ScenarioClockConfig,
): string {
  if (!iso) return ''
  const parts = civilParts(iso, clock.timezone)
  return `${parts.hour.toString().padStart(2, '0')}:${parts.minute.toString().padStart(2, '0')}:${parts.second.toString().padStart(2, '0')}`
}

/** Story boards always author in wall time. */
export function authorClockUsesWallTime(_clock?: ScenarioClockConfig): boolean {
  return true
}

/** Authoring fields: story instant in the session timezone. */
export function formatAuthorTime(iso: string, clock: ScenarioClockConfig): string {
  return formatStoryClock(iso, clock.timezone, storyClockWithSeconds(clock))
}

/** Running strip clock — story instant in the session timezone. */
export function formatRunningClock(playheadIso: string, clock: ScenarioClockConfig): string {
  const label = formatStoryClock(playheadIso, clock.timezone, storyClockWithSeconds(clock))
  if (!clock.showTimeZone) return label
  const zone = timeZoneAbbrev(playheadIso, clock.timezone)
  return zone ? `${label} ${zone}` : label
}

/**
 * Parse authoring time to a story ISO on the scenario's calendar day.
 * Wall `H:MM` / `H:MM:SS` / am/pm, or relative `m:ss` / `h:mm:ss` from scenario start.
 * Bare `10:53` uses the meridian that falls in the scenario window — never
 * silent fallthrough to relative elapsed.
 */
export function parseAuthorTime(text: string, clock: ScenarioClockConfig): string | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  if (looksLikeClockTime(trimmed)) {
    const startParts = civilParts(clock.startsAt, clock.timezone)
    const startSec = startParts.hour * 3600 + startParts.minute * 60 + startParts.second
    const endSec = clock.durationMs > 0 ? startSec + clock.durationMs / 1000 : null
    const wall = parseWallClockInputToSeconds(trimmed, { startSec, endSec })
    if (wall == null) return null
    return wallSecondsToIso(wall, clock)
  }
  const elapsed = parseElapsed(trimmed)
  if (elapsed == null) return null
  return addStoryMs(clock.startsAt, elapsed)
}

export const POSE_CSV_HEADER = '# time, lat, lng'

/** ~40 mph suburban / emergency-response average — shared by playback and CSV import. */
export const POSE_SPEED_MPS = 18
export const POSE_MIN_TRAVEL_MS = 350
/** Holds shorter than this are treated as noise / pass-through in the UI. */
export const POSE_HOLD_DISPLAY_MS = 500
/** Default author-visible clock gap when Add places the next waypoint (5s for easy Preview testing). */
export const WAYPOINT_STEP_MS = 5_000

/** Advance a story instant by `deltaMs`. Story time is 1:1 with milliseconds. */
export function addAuthorVisibleMs(iso: string, deltaMs: number, _clock?: ScenarioClockConfig): string {
  return addStoryMs(iso, Math.round(deltaMs))
}

/** Durations are already milliseconds. Kept so hold badges stay on the same call. */
export function boardDeltaToAuthorVisibleMs(boardDeltaMs: number, _clock?: ScenarioClockConfig): number {
  return Math.max(0, Math.round(boardDeltaMs))
}

/** Hold badge countdown — ceil so sub-second remainders don't read as 0:00. */
export function formatHoldCountdown(ms: number): string {
  const clamped = Math.max(0, Math.ceil(ms / 1000))
  const h = Math.floor(clamped / 3600)
  const m = Math.floor((clamped % 3600) / 60)
  const s = clamped % 60
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}

export type PoseFrame = {
  at: string
  lng: number
  lat: number
  holdMs?: number
}

function frameMs(frame: { at: string }): number {
  return storyMs(frame.at)
}

/**
 * Segment progress → blend factor.
 * Ease-out when arriving into a dwell (same-spot later time) or the last pin.
 */
export function poseSegmentBlend(tNorm: number, easeOutIntoStop: boolean): number {
  const t = Math.max(0, Math.min(1, tNorm))
  if (!easeOutIntoStop) return t
  return 1 - (1 - t) ** 3
}

/** @deprecated Prefer arrivesIntoDwell — kept for callers that still pass a hold duration. */
export function segmentEasesIntoStop(holdMs: number): boolean {
  return holdMs >= POSE_HOLD_DISPLAY_MS
}

function haversineM(a: { lng: number; lat: number }, b: { lng: number; lat: number }): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const la1 = toRad(a.lat)
  const la2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

function sameSpot(a: { lng: number; lat: number }, b: { lng: number; lat: number }): boolean {
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6
}

/** True when two poses are effectively co-located (shared map pin / dwell row). */
export function posesSameSpot(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
): boolean {
  return sameSpot(a, b)
}

/**
 * Contiguous same-spot runs in time order. Map pins use `label`; table/Preview use
 * frame indices within each run.
 */
export function poseLocationRuns(
  frames: readonly PoseFrame[],
): { startIndex: number; endIndex: number; lng: number; lat: number; label: number }[] {
  const sorted = normalizePoseKeyframes(frames)
  const runs: { startIndex: number; endIndex: number; lng: number; lat: number; label: number }[] =
    []
  let i = 0
  let label = 0
  while (i < sorted.length) {
    let j = i
    while (j + 1 < sorted.length && sameSpot(sorted[j]!, sorted[j + 1]!)) j += 1
    label += 1
    runs.push({
      startIndex: i,
      endIndex: j,
      lng: sorted[i]!.lng,
      lat: sorted[i]!.lat,
      label,
    })
    i = j + 1
  }
  return runs
}

/** Ease into a pin that starts a dwell (next row same spot) or is the last pin. */
export function arrivesIntoDwell(frames: readonly PoseFrame[], arriveIndex: number): boolean {
  const sorted = normalizePoseKeyframes(frames)
  if (arriveIndex < 0 || arriveIndex >= sorted.length) return false
  if (arriveIndex >= sorted.length - 1) return true
  return sameSpot(sorted[arriveIndex]!, sorted[arriveIndex + 1]!)
}

export function waypointHoldMs(frame: { holdMs?: number }): number {
  const h = frame.holdMs ?? 0
  return Number.isFinite(h) && h > 0 ? h : 0
}

/** Natural travel duration A→B (uncapped). Zero when points are effectively co-located. */
export function naturalTravelMs(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
): number {
  const distM = haversineM(a, b)
  const naturalMs = distM <= 1 ? 0 : (distM / POSE_SPEED_MPS) * 1000
  return naturalMs <= 0 ? 0 : Math.max(POSE_MIN_TRAVEL_MS, naturalMs)
}

/**
 * Travel used inside a depart→arrive window:
 * natural speed, then speed up if the window is shorter than natural travel.
 */
export function travelMsInGap(
  a: { lng: number; lat: number },
  b: { lng: number; lat: number },
  gapMs: number,
): number {
  const span = Math.max(0, gapMs)
  let travel = naturalTravelMs(a, b)
  if (travel > span) travel = span
  return travel
}

/**
 * Sort by time, expand legacy holdMs into a same-spot stay row, keep distinct
 * times at the same place (dwell markers). Strips holdMs from the result.
 */
export function normalizePoseKeyframes<T extends PoseFrame>(frames: readonly T[]): T[] {
  const sorted = [...frames].sort((a, b) => compareStory(a.at, b.at))
  const expanded: T[] = []
  for (const frame of sorted) {
    const copy = { ...frame } as T
    const hold = waypointHoldMs(copy)
    delete (copy as PoseFrame).holdMs
    expanded.push(copy)
    if (hold > 0) {
      expanded.push({ ...copy, at: addStoryMs(frame.at, hold) } as T)
    }
  }
  expanded.sort((a, b) => compareStory(a.at, b.at))
  const out: T[] = []
  for (const frame of expanded) {
    const last = out[out.length - 1]
    if (last && storyMs(last.at) === storyMs(frame.at) && sameSpot(last, frame)) continue
    out.push(frame)
  }
  return out
}

/**
 * Legacy helper: turn holdMs into a same-spot stay row at arrive+hold.
 * Prefer authoring an explicit later time at the same coordinates.
 */
export function applyHoldAtWaypoint<T extends PoseFrame>(
  frames: readonly T[],
  index: number,
  holdMs: number,
): T[] {
  if (index < 0 || index >= frames.length) return normalizePoseKeyframes(frames)
  const sorted = normalizePoseKeyframes(frames)
  if (index >= sorted.length) return sorted
  const cur = sorted[index]!
  const nextHold = Math.max(0, Math.round(holdMs))
  if (nextHold < POSE_HOLD_DISPLAY_MS) return sorted
  const stayAt = uniquePoseAt(addStoryMs(cur.at, nextHold), sorted)
  const stay = { ...cur, at: stayAt } as T
  delete (stay as PoseFrame).holdMs
  return normalizePoseKeyframes([...sorted, stay])
}

/**
 * Append a same-place stay (hold) after the location run containing `atIndex`.
 * Default gap is WAYPOINT_STEP_MS on the author-visible clock, clamped before the
 * next distinct location so the unit does not leave and come back.
 */
export function appendPoseHold<T extends PoseFrame>(
  frames: readonly T[],
  atIndex: number,
  clock?: ScenarioClockConfig,
): T[] {
  const sorted = normalizePoseKeyframes(frames)
  if (!sorted.length || atIndex < 0 || atIndex >= sorted.length) return sorted
  let end = atIndex
  while (end + 1 < sorted.length && sameSpot(sorted[end]!, sorted[end + 1]!)) end += 1
  const anchor = sorted[end]!
  const nextDistinct = sorted.find((f, i) => i > end && !sameSpot(f, anchor))
  const preferred = addAuthorVisibleMs(anchor.at, WAYPOINT_STEP_MS, clock)
  let stayAt = preferred
  if (nextDistinct) {
    const latestMs = frameMs(nextDistinct) - 1
    if (storyMs(stayAt) > latestMs) {
      const mid = Math.floor((frameMs(anchor) + frameMs(nextDistinct)) / 2)
      stayAt = isoFromStoryMs(Math.max(frameMs(anchor) + 1, mid))
    }
  }
  stayAt = uniquePoseAt(stayAt, sorted)
  const stay = { at: stayAt, lng: anchor.lng, lat: anchor.lat } as T
  return normalizePoseKeyframes([...sorted, stay])
}

/**
 * Append a new path pin offset from the last so it is a distinct map location.
 * Arrival advances the author-visible clock by WAYPOINT_STEP_MS.
 */
export function appendPoseWaypoint<T extends PoseFrame>(
  frames: readonly T[],
  clock?: ScenarioClockConfig,
): T[] {
  const sorted = normalizePoseKeyframes(frames)
  const last = sorted[sorted.length - 1]
  if (!last) return sorted
  // ~40 m step, fanned so successive Adds don't stack on each other.
  const stepM = 40
  const ang = sorted.length * 1.15
  const mPerDegLat = 111_320
  const mPerDegLng = Math.max(1e-6, 111_320 * Math.cos((last.lat * Math.PI) / 180))
  const lat = last.lat + (stepM * Math.sin(ang)) / mPerDegLat
  const lng = last.lng + (stepM * Math.cos(ang)) / mPerDegLng
  const stepped = addAuthorVisibleMs(last.at, WAYPOINT_STEP_MS, clock)
  const at = uniquePoseAt(stepped, sorted)
  const next = { at, lng, lat } as T
  return normalizePoseKeyframes([...sorted, next])
}

/** Pick an arrival time not already used by another pin (nudge +1ms on collision). */
export function uniquePoseAt(
  iso: string,
  frames: readonly { at: string }[],
  exceptIndex?: number,
): string {
  let t = storyMs(iso)
  const taken = new Set<number>()
  for (let i = 0; i < frames.length; i++) {
    if (i === exceptIndex) continue
    taken.add(storyMs(frames[i]!.at))
  }
  while (taken.has(t)) t += 1
  return isoFromStoryMs(t)
}

/** Scenario-time span covered by a path preview (first time mark → last). */
export function pathPreviewSpanMs(frames: readonly PoseFrame[]): number {
  const sorted = normalizePoseKeyframes(frames)
  if (sorted.length < 2) return 0
  return Math.max(0, storyDeltaMs(sorted[0]!.at, sorted[sorted.length - 1]!.at))
}

/** Scenario-time span for visibility Preview (first keyframe → last). */
export function visibilityPreviewSpanMs(frames: readonly { at: string }[]): number {
  if (frames.length < 2) return 0
  const sorted = [...frames].sort((a, b) => compareStory(a.at, b.at))
  return Math.max(0, storyDeltaMs(sorted[0]!.at, sorted[sorted.length - 1]!.at))
}

/** Active visibility row index at the playhead (last keyframe whose time has fired). */
export function visibilityPreviewIndexAtElapsed(
  frames: readonly { at: string }[],
  playheadIso: string,
): number {
  if (!frames.length) return 0
  const sorted = [...frames].sort((a, b) => compareStory(a.at, b.at))
  let idx = 0
  for (let i = 0; i < sorted.length; i++) {
    if (compareStory(sorted[i]!.at, playheadIso) <= 0) idx = i
    else break
  }
  return idx
}

/** Scenario-time span for number/radius Preview (first keyframe → last). */
export function numberPreviewSpanMs(frames: readonly { at: string }[]): number {
  return visibilityPreviewSpanMs(frames)
}

/** Active number/radius row index at the playhead (last keyframe whose time has fired). */
export function numberPreviewIndexAtElapsed(
  frames: readonly { at: string }[],
  playheadIso: string,
): number {
  return visibilityPreviewIndexAtElapsed(frames, playheadIso)
}

/**
 * How pose moves between time marks.
 * - `arrive-on-time` (vehicles): wait at the current pin, then travel so the next pin is hit on time.
 * - `continuous` (tornado track): lerp across the full gap between distinct marks (storm tracks smoothly).
 */
export type PoseMotionStyle = 'arrive-on-time' | 'continuous'

/**
 * Sample pose + UI pin index at scenario elapsed (for path Preview synced to strip rate).
 * Sequence index follows time markers; holding = waiting at a pin before the next move
 * (`arrive-on-time` only — continuous motion holds only on same-spot dwells).
 */
export function posePreviewAtElapsed(
  frames: readonly PoseFrame[],
  playheadIso: string,
  motion: PoseMotionStyle = 'arrive-on-time',
): PoseMotionSample | null {
  const elapsedMs = storyMs(playheadIso)
  const sorted = normalizePoseKeyframes(frames)
  if (!sorted.length) return null
  const pose =
    poseFromPoseFrames(sorted, playheadIso, false, motion) ??
    ({ lng: sorted[0]!.lng, lat: sorted[0]!.lat } as const)
  let waypointIndex = 0
  let holding = false
  let holdRemainingMs = 0
  let holdTotalMs = 0
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]!
    const next = sorted[i + 1]
    if (elapsedMs < frameMs(cur)) {
      waypointIndex = i
      break
    }
    if (!next) {
      waypointIndex = i
      break
    }
    const window = Math.max(0, frameMs(next) - frameMs(cur))
    if (motion === 'continuous') {
      if (sameSpot(cur, next)) {
        if (elapsedMs < frameMs(next)) {
          waypointIndex = i
          holding = window >= POSE_HOLD_DISPLAY_MS
          holdTotalMs = window
          holdRemainingMs = Math.max(0, frameMs(next) - elapsedMs)
          break
        }
        waypointIndex = i + 1
        continue
      }
      if (elapsedMs < frameMs(next)) {
        waypointIndex = i
        break
      }
      waypointIndex = i + 1
      continue
    }
    const travel = travelMsInGap(cur, next, window)
    const depart = frameMs(next) - travel
    if (elapsedMs < depart) {
      waypointIndex = i
      const wait = depart - frameMs(cur)
      holding = wait >= POSE_HOLD_DISPLAY_MS || travel <= 0
      holdTotalMs = Math.max(0, wait)
      holdRemainingMs = Math.max(0, depart - elapsedMs)
      break
    }
    if (elapsedMs < frameMs(next)) {
      if (travel > 0 && elapsedMs >= depart && elapsedMs < frameMs(next)) {
        waypointIndex = i
      } else {
        waypointIndex = i + 1
      }
      break
    }
    waypointIndex = i
  }
  return {
    pose,
    at: playheadIso,
    waypointIndex,
    holding,
    holdRemainingMs,
    holdTotalMs,
  }
}

export type PoseHoldLive = {
  holding: boolean
  holdTotalMs: number
  holdRemainingMs: number
}

/**
 * Pose at scenario elapsed.
 * `arrive-on-time` (vehicles): be at each pin at its time; wait at the current pin, then travel
 * so the next pin is hit on time (speed up only when the gap is too short).
 * Same coordinates at two times = dwell (no motion between those rows).
 * `continuous` (tornado): interpolate evenly across the whole window between distinct places.
 */
export function poseFromPoseFrames(
  frames: readonly PoseFrame[],
  playheadIso: string,
  loop = false,
  motion: PoseMotionStyle = 'arrive-on-time',
): { lng: number; lat: number } | null {
  if (!frames.length) return null
  const sorted = normalizePoseKeyframes(frames)
  let t = storyMs(playheadIso)
  if (loop) {
    const start = frameMs(sorted[0]!)
    const end = frameMs(sorted[sorted.length - 1]!)
    const span = end - start
    if (span > 0) {
      let rel = (t - start) % span
      if (rel < 0) rel += span
      t = start + rel
    }
  }
  const first = sorted[0]!
  if (t <= frameMs(first)) return { lng: first.lng, lat: first.lat }

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i]!
    const next = sorted[i + 1]
    if (!next) return { lng: cur.lng, lat: cur.lat }
    if (t < frameMs(cur)) return { lng: cur.lng, lat: cur.lat }

    const window = Math.max(0, frameMs(next) - frameMs(cur))
    if (motion === 'continuous') {
      if (sameSpot(cur, next) || window <= 0 || t >= frameMs(next)) {
        if (t < frameMs(next)) return { lng: cur.lng, lat: cur.lat }
        continue
      }
      const u = poseSegmentBlend((t - frameMs(cur)) / window, arrivesIntoDwell(sorted, i + 1))
      return {
        lng: cur.lng + (next.lng - cur.lng) * u,
        lat: cur.lat + (next.lat - cur.lat) * u,
      }
    }

    const travel = travelMsInGap(cur, next, window)
    const depart = frameMs(next) - travel

    if (t < depart || travel <= 0) {
      if (t < frameMs(next)) return { lng: cur.lng, lat: cur.lat }
      continue
    }
    if (t >= frameMs(next)) continue

    const local = t - depart
    const u = poseSegmentBlend(local / Math.max(1, travel), arrivesIntoDwell(sorted, i + 1))
    return {
      lng: cur.lng + (next.lng - cur.lng) * u,
      lat: cur.lat + (next.lat - cur.lat) * u,
    }
  }

  const last = sorted[sorted.length - 1]!
  return { lng: last.lng, lat: last.lat }
}

export type PoseMotionSample = {
  pose: { lng: number; lat: number }
  /** Playhead story instant this sample was taken at. */
  at: string
  /** Active time-marker index for UI (table / Preview). */
  waypointIndex: number
  holding: boolean
  holdRemainingMs: number
  holdTotalMs: number
}

/** Wall duration of path motion: waits + natural travel between distinct places. */
export function poseMotionDurationMs(frames: readonly PoseFrame[]): number {
  const sorted = normalizePoseKeyframes(frames)
  if (sorted.length < 2) return 0
  return Math.max(0, frameMs(sorted[sorted.length - 1]!) - frameMs(sorted[0]!))
}

/** Sample pose along preview-path motion timeline (wall-clock from path start). */
export function poseAtMotionMs(frames: readonly PoseFrame[], motionMs: number): PoseMotionSample | null {
  const sorted = normalizePoseKeyframes(frames)
  if (!sorted.length) return null
  return posePreviewAtElapsed(sorted, addStoryMs(sorted[0]!.at, Math.max(0, motionMs)))
}

/** Build pose keyframes from lat/lng only; times follow distance at ~40 mph.
 * Consecutive identical coordinates become a hold (default WAYPOINT_STEP_MS on the board clock).
 */
export function poseFramesFromLatLng(
  points: readonly { lat: number; lng: number }[],
  startIso: string,
): PoseFrame[] {
  let t = storyMs(startIso)
  const frames: PoseFrame[] = []
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!
    if (i > 0) {
      const prev = points[i - 1]!
      const travel = naturalTravelMs(prev, p)
      // Same place → hold gap so normalize keeps both rows (0 travel would collapse times).
      t += travel > 0 ? travel : WAYPOINT_STEP_MS
    }
    frames.push({ at: isoFromStoryMs(Math.round(t)), lat: p.lat, lng: p.lng })
  }
  return frames
}

/** Where leftover wait goes when a story window is longer than ~40 mph travel. */
export type PoseFitHold = 'origin' | 'destination'

/**
 * Story-window route timing (seed + Inspect Retime):
 * - Drive ~40 mph along the points between start and end
 * - `origin` (arrive at end time): hold on the first pin, then roll so arrival ≈ end
 * - `destination` (leave at start time): leave at start, hold on the last pin until end
 * - Natural travel longer than the window → uniform compress so the last pin is at end
 */
export function posePathNaturalInWindow(
  startIso: string,
  endIso: string,
  points: readonly { lng: number; lat: number }[],
  hold: PoseFitHold = 'destination',
  /** Cap a start-of-route wait. The truck still finishes on the end pin. */
  originHoldMs?: number,
): PoseFrame[] {
  if (points.length === 0) return []
  const start = storyMs(startIso)
  const end = Math.max(start, storyMs(endIso))
  const first = points[0]!
  const lastPt = points[points.length - 1]!

  if (points.length === 1) {
    if (end > start) {
      return [
        { at: isoFromStoryMs(start), lng: first.lng, lat: first.lat },
        { at: isoFromStoryMs(end), lng: first.lng, lat: first.lat },
      ]
    }
    return [{ at: isoFromStoryMs(start), lng: first.lng, lat: first.lat }]
  }

  const naturalFromStart = poseFramesFromLatLng(points, isoFromStoryMs(start))
  const naturalArrive = frameMs(naturalFromStart[naturalFromStart.length - 1]!)
  const travelMs = Math.max(0, naturalArrive - start)
  const windowMs = end - start

  if (travelMs > windowMs) {
    const span = Math.max(1, travelMs)
    const target = Math.max(1, windowMs)
    return naturalFromStart.map((f, i) =>
      i === 0
        ? { ...f, at: isoFromStoryMs(start) }
        : {
            ...f,
            at: isoFromStoryMs(Math.round(start + ((frameMs(f) - start) / span) * target)),
          },
    )
  }

  if (hold === 'origin') {
    const naturalDepart = end - travelMs
    const depart =
      originHoldMs != null ? Math.min(naturalDepart, start + Math.max(0, originHoldMs)) : naturalDepart
    const moving = poseFramesFromLatLng(points, isoFromStoryMs(depart))
    const arrived = frameMs(moving[moving.length - 1]!)
    if (depart <= start && arrived >= end) return moving
    const frames: PoseFrame[] = []
    if (depart > start) {
      frames.push({ at: isoFromStoryMs(start), lng: first.lng, lat: first.lat })
    }
    frames.push(...moving.map((f, i) => (i === 0 && depart > start ? { ...f, at: isoFromStoryMs(depart) } : f)))
    if (arrived < end - 500) {
      frames.push({ at: isoFromStoryMs(end), lng: lastPt.lng, lat: lastPt.lat })
    }
    return frames
  }

  if (naturalArrive >= end) return naturalFromStart
  return [
    ...naturalFromStart,
    { at: isoFromStoryMs(end), lng: lastPt.lng, lat: lastPt.lat },
  ]
}

/**
 * Retime an existing waypoint list at ~40 mph.
 * Keeps the first/last times of each transit leg and any same-place holds between legs.
 * `hold` chooses wait at start (origin) vs wait at end (destination) for each leg.
 */
export function retimePosePathNatural(
  frames: readonly PoseFrame[],
  hold: PoseFitHold = 'destination',
): PoseFrame[] {
  const sorted = normalizePoseKeyframes(frames)
  if (sorted.length === 0) return []
  if (sorted.length === 1) {
    const f = sorted[0]!
    return [{ at: f.at, lng: f.lng, lat: f.lat }]
  }

  const out: PoseFrame[] = []
  let i = 0
  while (i < sorted.length) {
    const cur = sorted[i]!

    if (i + 1 < sorted.length && sameSpot(cur, sorted[i + 1]!)) {
      let j = i + 1
      while (j + 1 < sorted.length && sameSpot(cur, sorted[j + 1]!)) j += 1
      pushPoseUnique(out, { at: cur.at, lng: cur.lng, lat: cur.lat })
      pushPoseUnique(out, { at: sorted[j]!.at, lng: cur.lng, lat: cur.lat })
      i = j + 1
      continue
    }

    const startIdx = i
    const points: { lng: number; lat: number }[] = [{ lng: cur.lng, lat: cur.lat }]
    let j = i
    while (j + 1 < sorted.length && !sameSpot(sorted[j]!, sorted[j + 1]!)) {
      j += 1
      const pt = sorted[j]!
      points.push({ lng: pt.lng, lat: pt.lat })
    }
    let endIdx = j
    if (j + 1 < sorted.length && sameSpot(sorted[j]!, sorted[j + 1]!)) {
      let k = j + 1
      while (k + 1 < sorted.length && sameSpot(sorted[j]!, sorted[k + 1]!)) k += 1
      endIdx = k
    }

    const leg = posePathNaturalInWindow(sorted[startIdx]!.at, sorted[endIdx]!.at, points, hold)
    for (const f of leg) pushPoseUnique(out, f)
    i = endIdx + 1
  }
  return out
}

/** Inspect Retime intents — whole path as one transit (v1). */
export type PoseRetimeIntent = 'arrive-at-end' | 'leave-at-start' | 'natural-only'

/**
 * Retime the full waypoint list as a single route (hand-authored paths).
 * Start = first row time. End = `endIso` (required except natural-only).
 * Geometry = unique places in order (mid-route Add-hold waits are collapsed in v1).
 */
export function retimePosePathWithIntent(
  frames: readonly PoseFrame[],
  intent: PoseRetimeIntent,
  endIso?: string,
): PoseFrame[] {
  const sorted = normalizePoseKeyframes(frames)
  if (sorted.length === 0) return []
  if (sorted.length === 1) {
    const f = sorted[0]!
    return [{ at: f.at, lng: f.lng, lat: f.lat }]
  }

  const startIso = sorted[0]!.at
  const points: { lng: number; lat: number }[] = []
  for (const f of sorted) {
    const prev = points[points.length - 1]
    if (!prev || !sameSpot(prev, f)) points.push({ lng: f.lng, lat: f.lat })
  }

  if (intent === 'natural-only') {
    return poseFramesFromLatLng(points, startIso)
  }

  const end = endIso ?? sorted[sorted.length - 1]!.at
  const hold: PoseFitHold = intent === 'arrive-at-end' ? 'origin' : 'destination'
  return posePathNaturalInWindow(startIso, end, points, hold)
}

function pushPoseUnique(out: PoseFrame[], frame: PoseFrame): void {
  const last = out[out.length - 1]
  if (last && storyMs(last.at) === storyMs(frame.at) && sameSpot(last, frame)) return
  out.push(frame)
}

export function parsePosePaste(
  text: string,
  startIso: string,
  clock?: ScenarioClockConfig,
): PoseFrame[] | null {
  const lines = text
    .split(/\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#'))
  if (!lines.length) return null

  type Row = {
    lat: number
    lng: number
    holdMs: number
    /** Present when the line carried an explicit time (lossless path). */
    at?: string
  }
  const rows: Row[] = []

  for (const line of lines) {
    const parts = line.split(/[,\s]+/).map((p) => p.trim()).filter(Boolean)
    if (parts.length < 2) return null

    let lat: number
    let lng: number
    let at: string | undefined
    let holdRaw: string | undefined

    const first = parts[0]!
    const firstIsIntMs = /^\d+$/.test(first)
    const firstIsTime = first.includes(':') && !first.includes('.')

    if (parts.length >= 3 && (firstIsIntMs || firstIsTime)) {
      // time, lat, lng [, hold] — integer column is ms from scenario start
      lat = Number(parts[1])
      lng = Number(parts[2])
      holdRaw = parts[3]
      if (firstIsIntMs) {
        const origin = clock?.startsAt ?? startIso
        at = addStoryMs(origin, Number(first))
      } else if (clock) {
        const parsed = parseAuthorTime(first, clock)
        if (parsed == null) return null
        at = parsed
      } else {
        const parsed = parseElapsed(first)
        if (parsed == null) return null
        at = addStoryMs(startIso, parsed)
      }
    } else if (parts.length >= 2) {
      // lat, lng [, hold] — times inferred from distance
      lat = Number(parts[0])
      lng = Number(parts[1])
      holdRaw = parts[2]
    } else {
      return null
    }

    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null

    let holdMs = 0
    if (holdRaw != null && holdRaw !== '' && holdRaw !== '—' && holdRaw !== '-' && holdRaw !== '0') {
      const parsed = parseElapsed(holdRaw)
      if (parsed != null) holdMs = parsed
    }
    rows.push({
      lat,
      lng,
      holdMs,
      ...(at != null ? { at } : {}),
    })
  }

  const allTimed = rows.every((r) => r.at != null)
  let frames: PoseFrame[]
  if (allTimed) {
    frames = rows.map((r) => ({
      at: r.at!,
      lat: r.lat,
      lng: r.lng,
    }))
  } else {
    frames = poseFramesFromLatLng(rows, startIso)
  }

  for (let i = 0; i < rows.length; i++) {
    const hold = rows[i]!.holdMs
    if (hold >= POSE_HOLD_DISPLAY_MS) frames = applyHoldAtWaypoint(frames, i, hold)
  }
  return normalizePoseKeyframes(frames)
}

/**
 * Waypoint CSV using scenario clock times (HH:MM:SS when offset/script is set).
 * Import accepts the same shape, or lat,lng-only (times inferred from distance).
 * Bare integer first columns are still accepted as board ms for older pastes.
 */
export function formatPosePaste(
  frames: readonly PoseFrame[],
  clock?: ScenarioClockConfig,
): string {
  const body = normalizePoseKeyframes(frames)
    .map((f) => {
      const time = clock ? formatAuthorTime(f.at, clock) : f.at
      return `${time}, ${f.lat.toFixed(7)}, ${f.lng.toFixed(7)}`
    })
    .join('\n')
  return body ? `${POSE_CSV_HEADER}\n${body}` : POSE_CSV_HEADER
}

export function authorTimePlaceholder(_clock: ScenarioClockConfig): string {
  return 'e.g. 10:53:00 AM'
}

export function authorTimeHint(_clock: ScenarioClockConfig): string {
  return 'e.g. 10:53 AM'
}

/** Timeline stamp: story instant in the session timezone. */
export function formatTimelineStamp(
  iso: string,
  timeZone: string,
  withSeconds = false,
  withMeridiem = true,
): string {
  return formatStoryClock(iso, timeZone, withSeconds, withMeridiem)
}

/** Scenario distances are stored in meters; tiles often use feet/inches. */
export function formatDistanceM(meters: number, units: DisplayUnits): string {
  if (units === 'imperial') {
    const ft = meters * 3.28084
    if (ft >= 5280) {
      const mi = ft / 5280
      // Prefer whole miles when within ~1% (preset buttons stay "3 mi" not "3.00 mi").
      if (Math.abs(mi - Math.round(mi)) < 0.01) return `${Math.round(mi)} mi`
      return `${mi >= 10 ? Math.round(mi) : mi.toFixed(1)} mi`
    }
    return `${Math.round(ft)} ft`
  }
  if (meters >= 1000) {
    const km = meters / 1000
    if (Math.abs(km - Math.round(km)) < 0.01) return `${Math.round(km)} km`
    return `${(meters / 1000).toFixed(2)} km`
  }
  return `${Math.round(meters)} m`
}

/** Design distances in round US feet/miles → meters for storage (imperial-first presets). */
export function metersFromFeet(ft: number): number {
  return ft / 3.28084
}

export function metersFromMiles(mi: number): number {
  return mi * 1609.344
}

/** Compact distance for dense tables (e.g. nearby-asset Distance column).
 * Respects session displayUnits (imperial → ft/mi, metric → m/km). */
export function formatDistanceCompact(meters: number, units: DisplayUnits): string {
  if (units === 'imperial') {
    const ft = meters * 3.28084
    if (ft >= 5280) {
      const mi = ft / 5280
      return `${mi >= 10 ? Math.round(mi) : mi.toFixed(1)} mi`
    }
    // Explicit "ft" (not ′) so the unit reads clearly in the dense table.
    return `${Math.round(ft)} ft`
  }
  if (meters >= 1000) {
    const km = meters / 1000
    return `${km >= 10 ? Math.round(km) : km.toFixed(1)} km`
  }
  return `${Math.round(meters)} m`
}

export function metersToDisplay(meters: number, units: DisplayUnits): number {
  return units === 'imperial' ? Math.round(meters * 3.28084) : Math.round(meters)
}

export function displayToMeters(value: number, units: DisplayUnits): number {
  return units === 'imperial' ? value / 3.28084 : value
}

export function distanceUnitLabel(units: DisplayUnits): string {
  return units === 'imperial' ? 'Feet' : 'Meters'
}

export function distanceUnitShort(units: DisplayUnits): string {
  return units === 'imperial' ? 'ft' : 'm'
}

/** Width number field. Hurricane fields are miles (or km); other rings stay in feet (or m). */
export function widthInputNumber(diameterM: number, units: DisplayUnits, miles: boolean): number {
  if (!miles) return metersToDisplay(diameterM, units)
  if (units === 'metric') return Math.round((diameterM / 1000) * 10) / 10
  return Math.round((diameterM / 1609.344) * 10) / 10
}

export function widthInputToMeters(value: number, units: DisplayUnits, miles: boolean): number {
  if (!miles) return displayToMeters(value, units)
  if (units === 'metric') return value * 1000
  return value * 1609.344
}

export function widthInputSuffix(units: DisplayUnits, miles: boolean): string {
  if (!miles) return distanceUnitShort(units)
  return units === 'metric' ? 'km' : 'mi'
}

/** Wind speed is stored as mph; convert for display. */
export function formatSpeedMph(mph: number, units: DisplayUnits): string {
  if (units === 'metric') return `${Math.round(mph * 1.60934)} km/h`
  return `${Math.round(mph)} mph`
}

export function speedUnitLabel(units: DisplayUnits): string {
  return units === 'metric' ? 'km/h' : 'mph'
}

/** Near-duplicate window when remembering a camera beat (replace nearest). */
export const CAMERA_BEAT_MERGE_MS = 750

/** Default ease when a beat omits easeMs. */
export const DEFAULT_CAMERA_EASE_MS = 1_000

export function sortCameraBeats(beats: readonly CameraBeat[]): CameraBeat[] {
  return [...beats].sort((a, b) => frameMs(a) - frameMs(b))
}

/** Insert or replace a beat within CAMERA_BEAT_MERGE_MS of an existing stamp. */
export function upsertCameraBeat(
  beats: readonly CameraBeat[],
  next: CameraBeat,
  mergeMs = CAMERA_BEAT_MERGE_MS,
): CameraBeat[] {
  const sorted = sortCameraBeats(beats)
  let bestIdx = -1
  let bestDist = Infinity
  for (let i = 0; i < sorted.length; i++) {
    const d = Math.abs(frameMs(sorted[i]!) - frameMs(next))
    if (d <= mergeMs && d < bestDist) {
      bestDist = d
      bestIdx = i
    }
  }
  if (bestIdx >= 0) {
    sorted[bestIdx] = next
    return sortCameraBeats(sorted)
  }
  sorted.push(next)
  return sortCameraBeats(sorted)
}

/** Latest beat at or before elapsed (hold until the next beat). */
export function cameraBeatAtElapsed(
  beats: readonly CameraBeat[],
  playheadIso: string,
): CameraBeat | null {
  const elapsedMs = storyMs(playheadIso)
  let best: CameraBeat | null = null
  for (const b of beats) {
    if (frameMs(b) <= elapsedMs && (!best || frameMs(b) >= frameMs(best))) {
      best = b
    }
  }
  return best
}

/** Beat within the merge window of `atIso`, if any. */
export function cameraBeatNear(
  beats: readonly CameraBeat[],
  atIso: string,
  mergeMs = CAMERA_BEAT_MERGE_MS,
): CameraBeat | null {
  const atMs = storyMs(atIso)
  let best: CameraBeat | null = null
  let bestDist = Infinity
  for (const b of beats) {
    const d = Math.abs(frameMs(b) - atMs)
    if (d <= mergeMs && d < bestDist) {
      bestDist = d
      best = b
    }
  }
  return best
}

/** Drop beats within the merge window of `atIso`. */
export function removeCameraBeatNear(
  beats: readonly CameraBeat[],
  atIso: string,
  mergeMs = CAMERA_BEAT_MERGE_MS,
): CameraBeat[] | undefined {
  const atMs = storyMs(atIso)
  const next = beats.filter((b) => Math.abs(frameMs(b) - atMs) > mergeMs)
  return next.length ? sortCameraBeats(next) : undefined
}
